import type { GridPoint, LevelConfig, LevelState } from "./types";
import { createLevelState, getCell, getNodePair, pointKey, pointsEqual } from "./state";

export interface SolvedPath {
  readonly color: string;
  readonly cells: readonly GridPoint[];
}

export interface SolveLevelOptions {
  readonly maxSolutions?: number;
  readonly timeLimitMs?: number;
}

export interface SolveLevelResult {
  readonly levelId: string;
  readonly solutionCount: number;
  readonly hasSolution: boolean;
  readonly hasUniqueSolution: boolean;
  readonly timedOut: boolean;
  readonly elapsedMs: number;
  readonly checkedCandidateSets: number;
  readonly candidateCountsByColor: Readonly<Record<string, number>>;
  readonly firstSolution: readonly SolvedPath[] | null;
}

interface SearchContext {
  readonly startedAt: number;
  readonly timeLimitMs: number;
  timedOut: boolean;
}

interface CandidateGroup {
  readonly color: string;
  readonly candidates: readonly (readonly GridPoint[])[];
}

export function solveLevel(config: LevelConfig, options: SolveLevelOptions = {}): SolveLevelResult {
  const startedAt = Date.now();
  const context: SearchContext = {
    startedAt,
    timeLimitMs: options.timeLimitMs ?? 2_000,
    timedOut: false
  };
  const maxSolutions = options.maxSolutions ?? 2;
  const state = createLevelState(config);
  const colors = Array.from(state.nodesByColor.keys());
  const candidateCountsByColor: Record<string, number> = {};
  const groups: CandidateGroup[] = [];

  for (const color of colors) {
    const candidates = generatePathCandidates(state, color, context);
    candidateCountsByColor[color] = candidates.length;
    groups.push({ color, candidates });
    if (context.timedOut || candidates.length === 0) {
      return {
        levelId: state.id,
        solutionCount: 0,
        hasSolution: false,
        hasUniqueSolution: false,
        timedOut: context.timedOut,
        elapsedMs: Date.now() - startedAt,
        checkedCandidateSets: 0,
        candidateCountsByColor,
        firstSolution: null
      };
    }
  }

  const orderedGroups = [...groups].sort(
    (left, right) => left.candidates.length - right.candidates.length
  );
  const selected = new Map<string, readonly GridPoint[]>();
  const occupied = new Set<string>();
  let solutionCount = 0;
  let checkedCandidateSets = 0;
  let firstSolution: readonly SolvedPath[] | null = null;

  function search(groupIndex: number): void {
    if (context.timedOut || solutionCount >= maxSolutions) {
      return;
    }
    if (isTimedOut(context)) {
      context.timedOut = true;
      return;
    }

    if (groupIndex >= orderedGroups.length) {
      checkedCandidateSets += 1;
      if (state.fillRequired && occupied.size !== state.fillableCount) {
        return;
      }

      solutionCount += 1;
      if (!firstSolution) {
        firstSolution = colors.map((color) => ({
          color,
          cells: (selected.get(color) ?? []).map(clonePoint)
        }));
      }
      return;
    }

    const group = orderedGroups[groupIndex];
    if (!group) {
      return;
    }

    for (const candidate of group.candidates) {
      if (hasAnyOccupiedCell(candidate, occupied)) {
        continue;
      }

      selected.set(group.color, candidate);
      for (const point of candidate) {
        occupied.add(pointKey(point));
      }

      search(groupIndex + 1);

      for (const point of candidate) {
        occupied.delete(pointKey(point));
      }
      selected.delete(group.color);

      if (context.timedOut || solutionCount >= maxSolutions) {
        return;
      }
    }
  }

  search(0);

  return {
    levelId: state.id,
    solutionCount,
    hasSolution: solutionCount > 0,
    hasUniqueSolution: solutionCount === 1 && !context.timedOut,
    timedOut: context.timedOut,
    elapsedMs: Date.now() - startedAt,
    checkedCandidateSets,
    candidateCountsByColor,
    firstSolution
  };
}

function generatePathCandidates(
  state: LevelState,
  color: string,
  context: SearchContext
): readonly (readonly GridPoint[])[] {
  const pair = getNodePair(state, color);
  const path: GridPoint[] = [clonePoint(pair.origin.position)];
  const visited = new Set<string>([pointKey(pair.origin.position)]);
  const candidates: GridPoint[][] = [];

  function dfs(current: GridPoint): void {
    if (context.timedOut) {
      return;
    }
    if (isTimedOut(context)) {
      context.timedOut = true;
      return;
    }

    const remainingCells = pair.origin.targetLength - path.length;
    const distanceToTarget = manhattanDistance(current, pair.target.position);
    if (distanceToTarget > remainingCells || (remainingCells - distanceToTarget) % 2 !== 0) {
      return;
    }

    if (path.length === pair.origin.targetLength) {
      if (pointsEqual(current, pair.target.position)) {
        candidates.push(path.map(clonePoint));
      }
      return;
    }

    for (const next of getNeighbors(current)) {
      if (next.x < 0 || next.x >= state.width || next.y < 0 || next.y >= state.height) {
        continue;
      }

      const key = pointKey(next);
      if (visited.has(key)) {
        continue;
      }

      const cell = getCell(state, next);
      if (cell.isObstacle || (cell.hasNode && cell.nodeColor !== color)) {
        continue;
      }

      const nextLength = path.length + 1;
      const isTarget = pointsEqual(next, pair.target.position);
      if (isTarget && nextLength !== pair.origin.targetLength) {
        continue;
      }
      if (!isTarget && nextLength === pair.origin.targetLength) {
        continue;
      }

      visited.add(key);
      path.push(next);
      dfs(next);
      path.pop();
      visited.delete(key);
    }
  }

  dfs(pair.origin.position);
  return candidates;
}

function getNeighbors(point: GridPoint): readonly GridPoint[] {
  return [
    { x: point.x + 1, y: point.y },
    { x: point.x, y: point.y + 1 },
    { x: point.x - 1, y: point.y },
    { x: point.x, y: point.y - 1 }
  ];
}

function hasAnyOccupiedCell(
  candidate: readonly GridPoint[],
  occupied: ReadonlySet<string>
): boolean {
  return candidate.some((point) => occupied.has(pointKey(point)));
}

function manhattanDistance(left: GridPoint, right: GridPoint): number {
  return Math.abs(left.x - right.x) + Math.abs(left.y - right.y);
}

function isTimedOut(context: SearchContext): boolean {
  return Date.now() - context.startedAt > context.timeLimitMs;
}

function clonePoint(point: GridPoint): GridPoint {
  return { x: point.x, y: point.y };
}
